import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Shield, Target, Leaf, BookOpen, Users, TrendingUp,
  FlaskConical, Heart, Star, ArrowRight, CheckCircle2,
  Brain, Utensils, ChevronRight,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { SEOMeta } from '@/components/SEOMeta';
import { PageBreadcrumb } from '@/components/PageBreadcrumb';

const BREADCRUMBS = [{ label: 'За нас', href: '/about' }];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 22 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, delay, ease: 'easeOut' },
});

/* ── Team members ── */
const team = [
  {
    initials: 'МП',
    name: 'Мартина Попова',
    role: 'Нутриционист & Диетолог',
    bio: 'Сертифициран нутриционист с 9+ години опит в хранителни консултации и управление на теглото. Специализирала се в спортна нутриция и балансирани диети за различни цели.',
    color: 'from-emerald-400 to-emerald-600',
    expertise: ['Спортна нутриция', 'TDEE калкулации', 'Балансирани диети'],
  },
  {
    initials: 'АД',
    name: 'Александър Димитров',
    role: 'Основател & CEO',
    bio: 'Предприемач с фокус върху HealthTech и персонализирана медицина. Вярва, че технологиите трябва да правят здравословния начин на живот достъпен за всеки, независимо от бюджет.',
    color: 'from-slate-600 to-slate-800',
    expertise: ['HealthTech', 'Продуктова стратегия', 'Здравни технологии'],
  },
  {
    initials: 'ЕТ',
    name: 'Елена Тодорова',
    role: 'Главен диетолог',
    bio: 'Магистър по клинично хранене с опит в болнична диетология и превантивна медицина. Отговаря за научната база зад всички хранителни планове в платформата.',
    color: 'from-teal-400 to-emerald-600',
    expertise: ['Клинична диетология', 'Превантивна медицина', 'Рецептурен дизайн'],
  },
  {
    initials: 'БС',
    name: 'Борис Стефанов',
    role: 'Главен разработчик',
    bio: 'Full-stack инженер с опит в AI системи и медицински платформи. Изгради алгоритмите зад персонализираните планове и интеграцията с AI за генериране на менюта.',
    color: 'from-gray-600 to-gray-800',
    expertise: ['AI & Machine Learning', 'Full-stack разработка', 'Здравни системи'],
  },
];

/* ── Expertise cards ── */
const expertise = [
  {
    icon: Brain,
    title: 'Нутриционна наука',
    desc: 'Плановете ни се базират на доказани научни методи — формулите на Mifflin-St Jeor и Harris-Benedict за изчисляване на базов метаболизъм, одобрени от международни здравни организации.',
    color: 'bg-emerald-50 text-emerald-600',
  },
  {
    icon: FlaskConical,
    title: 'TDEE & Макронутриенти',
    desc: 'Екипът ни включва специалисти с дълбоко разбиране на общите дневни енергийни разходи, разпределение на макронутриенти и адаптиране на плановете към индивидуални цели.',
    color: 'bg-blue-50 text-blue-600',
  },
  {
    icon: Utensils,
    title: 'Балгарска кухня',
    desc: 'Менютата ни са адаптирани специално за българската хранителна култура и наличните продукти. Нямаме екзотични съставки — само познати, достъпни и вкусни ястия.',
    color: 'bg-amber-50 text-amber-600',
  },
  {
    icon: Heart,
    title: 'Холистичен подход',
    desc: 'Разглеждаме храненето като баланс — не като лишения. Включваме разнообразие от групи храни, оптимален прием на хранителни вещества и удоволствие от яденето.',
    color: 'bg-rose-50 text-rose-600',
  },
];

/* ── Values ── */
const values = [
  { icon: Shield, title: 'Прозрачност', desc: 'Показваме точно как са изчислени твоите калории и от какво се състоят плановете ти. Никакви скрити формули.' },
  { icon: Target, title: 'Персонализация', desc: 'Всеки план е уникален. Вземаме предвид метаболизъм, активност, цели, бюджет и хранителни предпочитания.' },
  { icon: Leaf, title: 'Достъпност', desc: 'Здравословното хранене не трябва да е луксозно. Предлагаме 3 бюджетни нива — от икономично до премиум.' },
];

/* ── Stats ── */
const stats = [
  { value: '500+', label: 'Активни потребители' },
  { value: '15 000+', label: 'Генерирани планове' },
  { value: '9+', label: 'Години опит в нутрицията' },
];

export default function AboutPage() {
  return (
    <>
      <SEOMeta
        title="За нас"
        description="Запознай се с екипа зад portion.bg — нутриционисти, диетолози и технолози, обединени от една цел: здравословно хранене за всеки."
        keywords="portion.bg екип, нутриционисти България, диетолог, хранителен план, TDEE"
        canonical="/about"
        breadcrumbs={BREADCRUMBS}
        schemas={[
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            '@id': 'https://portion.bg/about#webpage',
            url: 'https://portion.bg/about',
            name: 'За нас — portion.bg',
            description: 'Запознай се с екипа зад portion.bg — нутриционисти, диетолози и технолози, обединени от една цел: здравословно хранене за всеки.',
            inLanguage: 'bg-BG',
            isPartOf: { '@id': 'https://portion.bg/#website' },
            about: { '@id': 'https://portion.bg/#organization' },
          },
        ]}
      />
      <PageBreadcrumb items={BREADCRUMBS} />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14 space-y-24">

        {/* ── Hero ── */}
        <motion.section {...fadeUp(0)} className="text-center">
          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border mb-5 px-4 py-1.5">
            Нашата история
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-5 leading-tight tracking-tight">
            Екипът зад<br />
            <span className="text-emerald-600">portion.bg</span>
          </h1>
          <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto leading-relaxed mb-10">
            Ние сме екип от нутриционисти, диетолози и технолози, обединени от обща цел —
            да направим науката за храненето разбираема и достъпна за всеки bulgarian.
          </p>

          {/* Stats */}
          <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-16">
            {stats.map((s, i) => (
              <motion.div key={s.label} {...fadeUp(0.1 + i * 0.08)} className="text-center">
                <p className="text-3xl sm:text-4xl font-black text-gray-900 dark:text-gray-100 tracking-tight">{s.value}</p>
                <p className="text-sm text-slate-400 mt-0.5">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* ── Story section ── */}
        <motion.section {...fadeUp(0.1)} className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <Badge className="bg-slate-100 text-slate-600 dark:text-slate-300 border-slate-200 border mb-4">Как започна всичко</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-5 leading-snug">
              Родена от реална нужда
            </h2>
            <div className="space-y-4 text-slate-500 dark:text-slate-400 leading-relaxed">
              <p>
                portion.bg стартира като отговор на един прост въпрос: <em className="text-gray-700 dark:text-gray-200 not-italic font-medium">„Защо е толкова сложно да се храниш здравословно с нормален бюджет?"</em>
              </p>
              <p>
                Открихме, че повечето платформи предлагат или прекалено скъпи продукти, или прекалено общи планове. Нашият екип реши да го направи по различен начин — с персонализиран подход, научна база и реалистични рецепти от всеки магазин.
              </p>
              <p>
                Днес зад portion.bg стоят хора с над 9 години колективен опит в нутриционните науки, диетологията и здравните технологии.
              </p>
            </div>
            <div className="mt-6 space-y-2.5">
              {[
                'Планове, валидирани от сертифицирани нутриционисти',
                'Алгоритми, базирани на международни стандарти',
                'Менюта, адаптирани за българската хранителна култура',
              ].map(t => (
                <div key={t} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-600 dark:text-slate-300">{t}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-xl" style={{ aspectRatio: '4/3' }}>
              <img
                src="https://images.unsplash.com/photo-1585358682246-23acb1561f6b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800"
                alt="Нутриционист"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Floating badge */}
            <div className="absolute -bottom-4 -left-4 bg-white dark:bg-slate-800 rounded-2xl shadow-lg px-5 py-3 border border-gray-100 dark:border-slate-700">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <Star className="w-4.5 h-4.5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-400 leading-none mb-0.5">Верифицирани</p>
                  <p className="text-sm font-bold text-gray-900 dark:text-gray-100">Нутриционисти</p>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* ── Expertise ── */}
        <motion.section {...fadeUp(0.1)}>
          <div className="text-center mb-10">
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border mb-4">Нашата експертиза</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">Защо да ни се доверите</h2>
            <p className="text-slate-500 dark:text-slate-400 mt-3 max-w-xl mx-auto text-sm sm:text-base">
              Зад всеки план стои задълбочено познание в хранителните науки и технологиите.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {expertise.map((item, i) => (
              <motion.div key={item.title} {...fadeUp(0.05 * i)}>
                <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 h-full border border-gray-100 dark:border-slate-700 hover:shadow-md transition-shadow duration-200"
                  style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 6px 20px rgba(0,0,0,0.04)' }}>
                  <div className={`w-11 h-11 rounded-xl ${item.color} flex items-center justify-center mb-4`}
                    style={{ background: item.color.includes('emerald') ? '#ecfdf5' : item.color.includes('blue') ? '#eff6ff' : item.color.includes('amber') ? '#fffbeb' : '#fff1f2' }}>
                    <item.icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* ── Team ── */}
        <motion.section {...fadeUp(0.1)}>
          <div className="text-center mb-10">
            <Badge className="bg-slate-100 text-slate-600 dark:text-slate-300 border-slate-200 border mb-4">Нашият екип</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">Хората зад платформата</h2>
            <p className="text-slate-500 dark:text-slate-400 mt-3 max-w-xl mx-auto text-sm sm:text-base">
              portion.bg е изграден от страстен екип от специалисти с различна, но взаимно допълваща се експертиза.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {team.map((member, i) => (
              <motion.div key={member.name} {...fadeUp(0.07 * i)}>
                <div className="bg-white dark:bg-slate-800 rounded-2xl overflow-hidden border border-gray-100 dark:border-slate-700 hover:shadow-lg transition-all duration-250"
                  style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.05)' }}>
                  {/* Dark header */}
                  <div className="bg-gray-900 px-6 pt-6 pb-5">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${member.color} flex items-center justify-center shadow-lg flex-shrink-0`}>
                        <span className="text-lg font-black text-white">{member.initials}</span>
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-white">{member.name}</h3>
                        <p className="text-sm text-emerald-400 font-medium">{member.role}</p>
                      </div>
                    </div>
                  </div>
                  {/* Body */}
                  <div className="p-6">
                    <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed mb-4">{member.bio}</p>
                    <div className="flex flex-wrap gap-2">
                      {member.expertise.map(tag => (
                        <span key={tag} className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-gray-50 dark:bg-slate-900 text-gray-600 dark:text-gray-300 border border-gray-100 dark:border-slate-700">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* ── Values ── */}
        <motion.section {...fadeUp(0.1)}>
          <div className="text-center mb-10">
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border mb-4">Нашите ценности</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">Принципите, по които работим</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {values.map((v, i) => (
              <motion.div key={v.title} {...fadeUp(0.08 * i)}>
                <div className="text-center p-8 bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 h-full"
                  style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 6px 20px rgba(0,0,0,0.04)' }}>
                  <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
                    <v.icon className="w-5.5 h-5.5 text-emerald-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-3">{v.title}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{v.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* ── Mission CTA ── */}
        <motion.section {...fadeUp(0.1)}>
          <div className="bg-gray-900 rounded-3xl p-10 sm:p-14 text-center overflow-hidden relative">
            {/* Decorative dots */}
            <div className="absolute top-8 right-8 w-24 h-24 rounded-full opacity-[0.04]"
              style={{ background: '#059669', filter: 'blur(20px)' }} />
            <div className="absolute bottom-8 left-8 w-32 h-32 rounded-full opacity-[0.04]"
              style={{ background: '#059669', filter: 'blur(24px)' }} />

            <div className="relative">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6"
                style={{ background: 'rgba(5,150,105,0.15)', border: '1px solid rgba(5,150,105,0.25)' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <span className="text-xs font-semibold text-emerald-400 tracking-wide">НАШАТА МИСИЯ</span>
              </div>

              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-5 leading-snug max-w-2xl mx-auto">
                Да направим здравословното хранене достъпно за всеки
              </h2>
              <p className="text-slate-400 leading-relaxed max-w-xl mx-auto mb-8">
                Вярваме, че правилното хранене не трябва да е привилегия. С научен подход, истински екип и модерни технологии, помагаме на всеки да постигне целите си — независимо от бюджет или опит.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/calculator">
                  <button
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold text-white transition-all duration-150 hover:opacity-90"
                    style={{ background: '#059669', boxShadow: '0 0 24px rgba(5,150,105,0.30)' }}
                  >
                    Изчисли плана си <ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
                <Link to="/contact">
                  <button className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold text-white/70 hover:text-white border border-white/15 hover:border-white/30 transition-all duration-150">
                    Свържи се с нас <ChevronRight className="w-4 h-4" />
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </motion.section>

      </div>
    </>
  );
}
